import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useMeta } from "@/lib/site";
import { Overline, Reveal } from "@/components/Reveal";

export default function Journal() {
  useMeta("Journal", "Notes from the house of Bonheur Eternal — on scent, memory and the craft of perfume.");
  const { data: posts = [] } = useQuery({ queryKey: ["journal"], queryFn: async () => (await api.get("/journal")).data });

  return (
    <div className="pt-32 md:pt-44 pb-32 px-5 md:px-8 max-w-5xl mx-auto">
      <Reveal>
        <Overline>Journal</Overline>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2B2118] mt-4 tracking-tight">Notes from the house</h1>
      </Reveal>
      <div className="mt-20 divide-y divide-[#2B2118]/[0.06]">
        {posts.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.08}>
            <Link to={`/journal/${p.slug}`} data-testid={`journal-item-${p.slug}`} className="group grid md:grid-cols-12 gap-6 py-12 items-center">
              {p.image && (
                <div className="md:col-span-4 overflow-hidden border border-[#2B2118]/[0.07]">
                  <img src={p.image} alt="" loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
              )}
              <div className={p.image ? "md:col-span-8" : "md:col-span-12"}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#6E6151]">{p.date}</p>
                <h2 className="font-display text-2xl md:text-3xl text-[#2B2118] mt-3 group-hover:text-[#8F6B3A] transition-colors">{p.title}</h2>
                <p className="mt-4 text-[#5A4C3C] leading-relaxed line-clamp-2">{p.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function JournalPost() {
  const { slug } = useParams();
  const { data: p } = useQuery({ queryKey: ["journal", slug], queryFn: async () => (await api.get(`/journal/${slug}`)).data });
  useMeta(p?.title, p?.excerpt);
  if (!p) return <div className="min-h-screen" />;
  return (
    <article className="pt-32 md:pt-44 pb-32 px-5 md:px-8 max-w-3xl mx-auto">
      <Reveal>
        <Overline>Journal — {p.date}</Overline>
        <h1 className="font-display text-4xl md:text-5xl text-[#2B2118] mt-4 tracking-tight leading-tight" data-testid="journal-post-title">{p.title}</h1>
      </Reveal>
      {p.image && (
        <Reveal delay={0.1} className="mt-12 overflow-hidden border border-[#2B2118]/[0.07]">
          <img src={p.image} alt="" className="w-full object-cover" />
        </Reveal>
      )}
      <Reveal delay={0.15} className="mt-12">
        {(p.content || "").split("\n\n").map((para, i) => (
          <p key={i} className="text-[#5A4C3C] leading-loose mb-6 text-base md:text-lg">{para}</p>
        ))}
      </Reveal>
    </article>
  );
}
